import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/apiClient';
import { API_ENDPOINTS } from '../config/api';

// Icons
import {
  ArrowPathIcon,
  ArrowUpRightIcon,
  ArrowDownRightIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  WalletIcon,
  ChevronRightIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CircleStackIcon
} from '@heroicons/react/24/outline';

// Currency Icons
const CurrencyIcon = ({ currency, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-10 h-10',
    '2xl': 'w-12 h-12'
  };

  const getIconColor = (currency) => {
    switch (currency) {
      case 'BAI': return 'text-emerald-500';
      case 'BNB': return 'text-yellow-500';
      case 'IMX': return 'text-blue-500';
      case 'USDT': return 'text-green-500';
      case 'ODP': return 'text-orange-500';
      default: return 'text-gray-500';
    }
  };

  return (
    <div className={`${sizeClasses[size]} ${getIconColor(currency)} flex items-center justify-center rounded-full bg-gray-800/50 p-1`}>
      {currency === 'BAI' && <BanknotesIcon className="w-full h-full" />}
      {currency === 'BNB' && <CurrencyDollarIcon className="w-full h-full" />}
      {currency === 'IMX' && <ArrowTrendingUpIcon className="w-full h-full" />}
      {currency === 'USDT' && <ArrowTrendingDownIcon className="w-full h-full" />}
      {currency === 'ODP' && <CircleStackIcon className="w-full h-full" />}
    </div>
  );
};

// Wallet Card Component
const WalletCard = ({ currency, balance, change, loading, onClaim }) => {
  const getCurrencyColor = (currency) => {
    switch (currency) {
      case 'BAI': return 'from-emerald-500 to-teal-500';
      case 'BNB': return 'from-yellow-500 to-amber-500';
      case 'IMX': return 'from-blue-500 to-indigo-500';
      case 'USDT': return 'from-green-500 to-emerald-500';
      case 'ODP': return 'from-orange-500 to-red-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getChangeColor = (change) => {
    if (change.startsWith('+')) return 'text-emerald-400';
    if (change.startsWith('-')) return 'text-rose-400';
    return 'text-gray-400';
  };

  return (
    <motion.div
      className="relative overflow-hidden rounded-xl bg-gray-900/50 backdrop-blur-sm border border-gray-800 hover:border-gray-700 transition-all duration-300 shadow-lg"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ scale: 1.02 }}
    >
      {/* Gradient background */}
      <div className="absolute inset-0 opacity-10 bg-gradient-to-br pointer-events-none">
        <div className={`absolute inset-0 bg-gradient-to-br ${getCurrencyColor(currency)}`}></div>
      </div>

      <div className="p-5">
        <div className="flex justify-between items-center mb-4">
          <CurrencyIcon currency={currency} size="lg" />
          <span className={`text-sm font-medium px-2 py-1 rounded-full ${getChangeColor(change)} bg-gray-800/50`}>
            {change}%
          </span>
        </div>

        <div className="mb-2">
          <h3 className="text-gray-400 text-sm font-medium">{currency} Balance</h3>
          {loading ? (
            <div className="h-8 w-24 bg-gray-800 animate-pulse rounded mt-1"></div>
          ) : (
            <div className="flex items-baseline">
              <span className="text-2xl font-bold text-white">{balance}</span>
              <span className="text-gray-500 ml-1 text-sm">{currency}</span>
            </div>
          )}
        </div>

        <button
          onClick={() => onClaim(currency)}
          className="w-full mt-3 py-2 px-4 rounded-lg bg-gray-800 hover:bg-gray-700 text-white text-sm font-medium transition-colors flex items-center justify-center space-x-1"
        >
          <span>Claim</span>
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>
    </motion.div>
  );
};

// Transaction Item Component
const TransactionItem = ({ transaction, index }) => {
  const { type, amount, currency, timestamp, description } = transaction;

  // Format timestamp
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <motion.div
      className="flex items-center justify-between p-4 border-b border-gray-800 hover:bg-gray-900/30 transition-colors rounded-lg"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
    >
      <div className="flex items-center space-x-3">
        <div className={`p-2 rounded-full ${type === 'in' ? 'bg-emerald-500/20' : 'bg-rose-500/20'}`}>
          {type === 'in' ? (
            <ArrowDownRightIcon className="h-5 w-5 text-emerald-500" />
          ) : (
            <ArrowUpRightIcon className="h-5 w-5 text-rose-500" />
          )}
        </div>
        <div>
          <p className="text-white font-medium">{description}</p>
          <div className="flex items-center text-gray-400 text-xs">
            <ClockIcon className="h-3 w-3 mr-1" />
            <span>{formatDate(timestamp)}</span>
          </div>
        </div>
      </div>
      <div className="text-right">
        <p className={`font-bold ${type === 'in' ? 'text-emerald-500' : 'text-rose-500'}`}>
          {type === 'in' ? '+' : '-'}{amount} {currency}
        </p>
      </div>
    </motion.div>
  );
};

// Claim Modal Component
const ClaimModal = ({ isOpen, onClose, currency, maxBalance, onSubmit }) => {
  const [walletAddress, setWalletAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!walletAddress) {
      newErrors.walletAddress = 'Wallet address is required';
    } else if (!/^0x[a-fA-F0-9]{40}$/.test(walletAddress)) {
      newErrors.walletAddress = 'Invalid wallet address format';
    }

    if (!amount) {
      newErrors.amount = 'Amount is required';
    } else if (isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    } else if (parseFloat(amount) > parseFloat(maxBalance)) {
      newErrors.amount = `Amount exceeds your ${currency} balance`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      onSubmit({
        currency,
        walletAddress,
        amount: parseFloat(amount)
      });
    }
  };

  const handleSetMax = () => {
    setAmount(maxBalance);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4 bg-black/70 backdrop-blur-sm">
      <motion.div
        className="relative w-full max-w-md bg-gray-900 rounded-xl p-6 shadow-xl border border-gray-800"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
      >
        <div className="flex items-center space-x-3 mb-6">
          <CurrencyIcon currency={currency} size="lg" />
          <h3 className="text-xl font-bold text-white">
            Claim {currency} to External Wallet
          </h3>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="walletAddress" className="block text-sm font-medium text-gray-300 mb-1">
                Destination Wallet Address (BNB Chain)
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="walletAddress"
                  value={walletAddress}
                  onChange={(e) => setWalletAddress(e.target.value)}
                  placeholder="0x..."
                  className={`w-full px-4 py-3 rounded-lg bg-gray-800 border ${
                    errors.walletAddress ? 'border-rose-500' : 'border-gray-700'
                  } text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-200`}
                />
                {errors.walletAddress && (
                  <p className="text-rose-500 text-xs mt-1 flex items-center">
                    <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                    {errors.walletAddress}
                  </p>
                )}
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <label htmlFor="amount" className="block text-sm font-medium text-gray-300">
                  Amount
                </label>
                <button
                  type="button"
                  onClick={handleSetMax}
                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors flex items-center"
                >
                  <span>Available: {maxBalance}</span>
                  <span className="ml-1 px-1.5 py-0.5 bg-blue-500/20 rounded text-[10px] hover:bg-blue-500/30">MAX</span>
                </button>
              </div>
              <div className="relative">
                <input
                  type="text"
                  id="amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className={`w-full px-4 py-3 rounded-lg bg-gray-800 border ${
                    errors.amount ? 'border-rose-500' : 'border-gray-700'
                  } text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-200 pr-16`}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <span className="text-gray-400">{currency}</span>
                </div>
                {errors.amount && (
                  <p className="text-rose-500 text-xs mt-1 flex items-center">
                    <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                    {errors.amount}
                  </p>
                )}
              </div>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
              <p className="text-sm text-gray-300 flex items-start">
                <ExclamationCircleIcon className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0 mt-0.5" />
                <span>
                  Claims are processed within 24-48 hours. Make sure the address is correct, as transactions cannot be reversed.
                </span>
              </p>
            </div>

            <div className="flex space-x-4 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-3 px-4 rounded-lg bg-gray-800 text-white font-medium
                  hover:bg-gray-700 transition-colors border border-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 py-3 px-4 rounded-lg bg-blue-600 text-white font-medium
                  transition-all transform hover:bg-blue-500 active:scale-95"
              >
                Claim Tokens
              </button>
            </div>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Success Modal Component
const SuccessModal = ({ isOpen, onClose, claimInfo }) => {
  if (!isOpen || !claimInfo) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4 bg-black/70 backdrop-blur-sm">
      <motion.div
        className="relative w-full max-w-md bg-gray-900 rounded-xl p-6 shadow-xl border border-gray-800 text-center"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
      >
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
          <CheckCircleIcon className="h-10 w-10 text-emerald-500" />
        </div>

        <h3 className="text-xl font-bold mb-4 text-white">Claim Submitted!</h3>

        <p className="text-gray-300 mb-6">
          Your claim for {claimInfo.amount} {claimInfo.currency} has been submitted for processing.
        </p>

        <div className="bg-gray-800 rounded-lg p-5 mb-6 text-left border border-gray-700">
          <div className="grid grid-cols-1 gap-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Destination:</span>
              <span className="text-white font-medium text-right">
                {claimInfo.walletAddress.slice(0, 6)}...{claimInfo.walletAddress.slice(-4)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Amount:</span>
              <div className="flex items-center">
                <CurrencyIcon currency={claimInfo.currency} size="sm" />
                <span className="text-white font-medium ml-1">{claimInfo.amount}</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Status:</span>
              <span className="text-amber-400 font-medium bg-amber-500/10 px-2 py-0.5 rounded-full text-xs">Pending</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">ETA:</span>
              <span className="text-white font-medium">24-48 hours</span>
            </div>
          </div>
        </div>

        <button
          onClick={onClose}
          className="w-full py-3 px-4 rounded-lg bg-blue-600 text-white font-medium
            transition-all transform hover:bg-blue-500 active:scale-95"
        >
          Close
        </button>
      </motion.div>
    </div>
  );
};

// Main Wallet Component
const Wallet = () => {
  const { user } = useAuth();
  const [balances, setBalances] = useState({
    BAI: '0',
    BNB: '0',
    IMX: '0',
    USDT: '0',
    ODP: '0'
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('balances');
  const [transactions, setTransactions] = useState([]);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [claimInfo, setClaimInfo] = useState(null);

  // Mock data for UI display
  const percentages = {
    BAI: '+2.4',
    BNB: '-0.8',
    IMX: '+5.3',
    USDT: '+0.1',
    ODP: '+1.2'
  };

  // Function to fetch wallet balances
  const fetchWalletBalances = useCallback(async () => {
    if (!user?.address) return;

    try {
      setLoading(true);
      // Use the wallet address to fetch balances
      const response = await api.get(API_ENDPOINTS.wallet.get(user.address));

      if (response) {
        setBalances({
          BAI: response.bai?.toString() || '0',
          BNB: response.bnb?.toString() || '0',
          IMX: response.imx?.toString() || '0',
          USDT: response.usdt?.toString() || '0',
          ODP: response.odp?.toString() || '0'
        });

        console.log('Wallet balances updated:', response);
      }
    } catch (error) {
      console.error('Error fetching wallet balances:', error);
      toast.error('Failed to load wallet balances');
    } finally {
      setLoading(false);
    }
  }, [user?.address]);

  // Function to fetch transaction history
  const fetchTransactionHistory = useCallback(async () => {
    if (!user?.address) return;

    try {
      const response = await api.get(API_ENDPOINTS.wallet.transactions(user.address));

      if (response && response.transactions) {
        setTransactions(response.transactions);
      } else {
        // Mock transactions for demo if none are returned
        setTransactions([
          {
            id: '1',
            type: 'in',
            amount: '100',
            currency: 'BAI',
            description: 'Quest Reward',
            timestamp: new Date().toISOString()
          },
          {
            id: '2',
            type: 'out',
            amount: '50',
            currency: 'BAI',
            description: 'Withdrawal',
            timestamp: new Date(Date.now() - 86400000).toISOString() // 1 day ago
          }
        ]);
      }
    } catch (error) {
      console.error('Error fetching transaction history:', error);
      // Set mock transactions for demo
      setTransactions([
        {
          id: '1',
          type: 'in',
          amount: '100',
          currency: 'BAI',
          description: 'Quest Reward',
          timestamp: new Date().toISOString()
        },
        {
          id: '2',
          type: 'out',
          amount: '50',
          currency: 'BAI',
          description: 'Withdrawal',
          timestamp: new Date(Date.now() - 86400000).toISOString() // 1 day ago
        }
      ]);
    }
  }, [user?.address]);

  // Initial data fetch
  useEffect(() => {
    fetchWalletBalances();
    fetchTransactionHistory();
  }, [fetchWalletBalances, fetchTransactionHistory]);

  // Set up polling for real-time updates
  useEffect(() => {
    if (!user?.address) return;

    // Poll for balance updates every 15 seconds
    const pollInterval = setInterval(() => {
      fetchWalletBalances();
    }, 15000);

    return () => clearInterval(pollInterval);
  }, [user?.address, fetchWalletBalances]);

  const handleRefresh = async () => {
    toast.promise(
      Promise.all([fetchWalletBalances(), fetchTransactionHistory()]),
      {
        loading: 'Refreshing wallet data...',
        success: 'Wallet refreshed successfully!',
        error: 'Failed to refresh wallet'
      }
    );
  };

  const handleClaimRequest = (currency) => {
    setSelectedCurrency(currency);
    setShowClaimModal(true);
  };

  const handleClaimSubmit = async (data) => {
    try {
      setLoading(true);

      // Make the API call to claim tokens
      const response = await api.post(API_ENDPOINTS.wallet.claimToExternal, {
        currency: data.currency,
        destinationAddress: data.walletAddress,
        amount: data.amount
      });

      console.log('Claim submitted:', response);

      // Close the claim modal
      setShowClaimModal(false);

      // Store claim info for success modal
      setClaimInfo({
        ...data,
        claimId: response.claimId,
        status: response.status
      });

      // Show success modal
      setShowSuccessModal(true);

      // Refresh balances
      await fetchWalletBalances();

      toast.success(`Claim for ${data.amount} ${data.currency} submitted successfully!`);
    } catch (error) {
      console.error('Error claiming tokens:', error);
      toast.error(error.message || 'Failed to process claim');
    } finally {
      setLoading(false);
    }
  };

  // Render balances section
  const renderBalancesSection = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <WalletCard
          currency="BAI"
          balance={balances.BAI}
          change={percentages.BAI}
          loading={loading}
          onClaim={handleClaimRequest}
        />
        <WalletCard
          currency="BNB"
          balance={balances.BNB}
          change={percentages.BNB}
          loading={loading}
          onClaim={handleClaimRequest}
        />
        <WalletCard
          currency="IMX"
          balance={balances.IMX}
          change={percentages.IMX}
          loading={loading}
          onClaim={handleClaimRequest}
        />
        <WalletCard
          currency="USDT"
          balance={balances.USDT}
          change={percentages.USDT}
          loading={loading}
          onClaim={handleClaimRequest}
        />
        <WalletCard
          currency="ODP"
          balance={balances.ODP}
          change={percentages.ODP}
          loading={loading}
          onClaim={handleClaimRequest}
        />
      </div>
    </div>
  );

  // Render transactions section
  const renderTransactionsSection = () => (
    <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800 overflow-hidden">
      <div className="p-4 border-b border-gray-800 flex justify-between items-center">
        <h3 className="text-lg font-medium text-white">Recent Transactions</h3>
        <button
          onClick={handleRefresh}
          className="text-sm text-gray-400 hover:text-white transition-colors flex items-center"
        >
          <ArrowPathIcon className="h-4 w-4 mr-1" />
          Refresh
        </button>
      </div>

      <div className="divide-y divide-gray-800">
        {transactions.length > 0 ? (
          transactions.map((transaction, index) => (
            <TransactionItem key={transaction.id} transaction={transaction} index={index} />
          ))
        ) : (
          <div className="p-8 text-center">
            <p className="text-gray-400">No transactions yet</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-950 text-white p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-block p-3 bg-blue-500/10 rounded-full mb-4">
            <WalletIcon className="h-8 w-8 text-blue-500" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Crypto Wallet</h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Manage your virtual tokens, track transactions, and claim to external wallets
          </p>
        </div>

        {/* Wallet not connected state */}
        {!user?.address && (
          <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 text-center max-w-md mx-auto border border-gray-800">
            <WalletIcon className="h-12 w-12 text-gray-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Connect Your Wallet</h2>
            <p className="text-gray-400 mb-6">
              Connect your wallet to view your balances and transaction history
            </p>
            <button className="px-6 py-3 bg-blue-600 hover:bg-blue-500 rounded-lg text-white font-medium transition-colors">
              Connect Wallet
            </button>
          </div>
        )}

        {/* Wallet connected state */}
        {user?.address && (
          <>
            {/* Refresh button */}
            <div className="flex justify-end mb-6">
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="px-4 py-2 bg-gray-900 hover:bg-gray-800 rounded-lg text-white text-sm font-medium transition-colors flex items-center space-x-2"
              >
                <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh Balances</span>
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-800 mb-6">
              <button
                onClick={() => setActiveTab('balances')}
                className={`px-4 py-2 text-sm font-medium border-b-2 ${
                  activeTab === 'balances'
                    ? 'border-blue-500 text-blue-500'
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                } transition-colors`}
              >
                Balances
              </button>
              <button
                onClick={() => setActiveTab('transactions')}
                className={`px-4 py-2 text-sm font-medium border-b-2 ${
                  activeTab === 'transactions'
                    ? 'border-blue-500 text-blue-500'
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                } transition-colors`}
              >
                Transactions
              </button>
            </div>

            {/* Content based on active tab */}
            <div className="mb-8">
              {activeTab === 'balances' && renderBalancesSection()}
              {activeTab === 'transactions' && renderTransactionsSection()}
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      <ClaimModal
        isOpen={showClaimModal}
        onClose={() => setShowClaimModal(false)}
        currency={selectedCurrency}
        maxBalance={selectedCurrency ? balances[selectedCurrency] : '0'}
        onSubmit={handleClaimSubmit}
      />

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        claimInfo={claimInfo}
      />
    </div>
  );
};

export default Wallet;
